import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LogOut, Loader2, Trophy, Heart, ArrowRight, Linkedin } from "lucide-react";
import { BrandLockup } from "../components/BrandLogo";
import { LINKEDIN_URL } from "../lib/social";
import { toast } from "sonner";
import api from "../lib/api";
import { firstName } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import Home from "../components/app/Home";
import Studio from "../components/studio/Studio";
import NewApplication from "../components/app/NewApplication";
import Tracker from "../components/app/Tracker";
import MyCv from "../components/app/MyCv";
import Community from "../components/app/Community";
import VoiceRefine from "../components/app/VoiceRefine";
import SupportModal from "../components/app/SupportModal";
import CareerJourney from "../components/app/CareerJourney";
import PrivacyCentre from "../components/app/PrivacyCentre";
import Profile from "../components/app/profile/Profile";
import { SideNav, MobileNav, VIEWS } from "../components/app/DashNav";
import { GUEST_CLAIM_KEY, GUEST_KEY } from "../components/studio/constants";
import ConsentGate from "../components/ConsentGate";
import { ThemeToggle } from "../components/ThemeToggle";

const ALIAS = { new: "studio", cv: "mycv" };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const raw = ALIAS[params.view] || params.view || "home";
  const view = VIEWS.includes(raw) ? raw : "home";
  const appId = view === "studio" ? params.appId || null : null;
  const [baseCv, setBaseCv] = useState(null);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const claiming = useRef(Boolean(localStorage.getItem(GUEST_CLAIM_KEY)));

  const refresh = useCallback(async () => {
    try {
      const [cvRes, statRes, appRes] = await Promise.all([api.get("/cv"), api.get("/stats"), api.get("/applications")]);
      setBaseCv(cvRes.data);
      setStats(statRes.data);
      setApplications(appRes.data);
    } catch (e) {
      toast.error("Failed to load your data");
    } finally {
      setLoaded(true);
    }
  }, []);

  const go = useCallback((v) => {
    const id = ALIAS[v] || v;
    navigate(id === "home" ? "/dashboard" : `/dashboard/${id}`);
    window.scrollTo({ top: 0 });
  }, [navigate]);
  const openApp = useCallback((id) => navigate(id ? `/dashboard/studio/${id}` : "/dashboard/studio"), [navigate]);

  useEffect(() => { if (!claiming.current) refresh(); }, [refresh]);

  useEffect(() => {
    const token = localStorage.getItem(GUEST_CLAIM_KEY);
    if (!token || !user?.consent_accepted) return;
    let alive = true;
    const attempt = async (n) => {
      try {
        const { data } = await api.post("/guest/claim", { token });
        localStorage.removeItem(GUEST_CLAIM_KEY);
        localStorage.removeItem(GUEST_KEY);
        if (data.jd_text) localStorage.setItem("pending_jd", data.jd_text);
        await refresh();
        toast.success(data.imported ? "We've saved the CV from your free check, so you won't need to upload it again." : "You already had a CV saved, so we kept that one.", { id: "guest-claim" });
        navigate("/dashboard/studio");
      } catch (e) {
        const status = e?.response?.status;
        if (alive && n < 6 && (status === 409 || !status)) { setTimeout(() => alive && attempt(n + 1), 4000); return; }
        localStorage.removeItem(GUEST_CLAIM_KEY);
        await refresh();
        toast.error(e?.response?.data?.detail || "We couldn't bring over your free check. Please upload your CV again.", { id: "guest-claim" });
      }
    };
    attempt(0);
    return () => { alive = false; };
  }, [refresh, navigate, user?.consent_accepted]);

  useEffect(() => {
    if (ALIAS[params.view]) navigate(`/dashboard/${ALIAS[params.view]}`, { replace: true });
  }, [params.view, navigate]);

  useEffect(() => {
    const welcome = new URLSearchParams(window.location.search).get("welcome");
    if (!welcome || !loaded || claiming.current) return;
    if (!baseCv) {
      navigate("/dashboard/mycv", { replace: true });
      toast.success(`Welcome${firstName(user) ? `, ${firstName(user)}` : ""}! We've added your name and photo from LinkedIn. Add your CV, or create one from your LinkedIn profile, to get started.`, { id: "welcome" });
    } else {
      navigate("/dashboard", { replace: true });
    }
  }, [loaded, baseCv, user, navigate]);

  return (
    <div className="min-h-screen bg-canvas text-ink flex">
      <aside className="hidden md:flex w-64 flex-col border-r border-line bg-surface sticky top-0 h-screen">
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-line">
          <a href="/" data-testid="home-logo-link" className="flex items-center group" title="Back to home" aria-label="GetInterviewed home">
            <BrandLockup />
          </a>
        </div>
        <SideNav view={view} onGo={go} isAdmin={user?.is_admin} />
        <div className="p-3 border-t border-line">
          <button data-testid="support-us-button" onClick={() => setSupportOpen(true)} className="mb-2 w-full gi-btn gi-btn-secondary gi-btn-sm">
            <Heart className="w-4 h-4 text-brand" aria-hidden="true" /> Support us
          </button>
          <button onClick={() => go("profile")} data-testid="sidebar-profile-link" title="My profile" className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-subtle transition-colors">
            {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-line" /> : (
              <div className="w-8 h-8 rounded-full bg-brand-soft text-brand-text flex items-center justify-center text-sm font-bold shrink-0" aria-hidden="true">{(user?.name || "?").trim().charAt(0).toUpperCase()}</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-ink truncate" data-testid="sidebar-user-name">{user?.name || "Your account"}</div>
              <div className="text-xs text-faint">View my profile</div>
            </div>
          </button>
          <button data-testid="logout-button" onClick={logout} className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-body hover:bg-subtle hover:text-danger transition-colors duration-200">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-line backdrop-blur-md bg-surface/90 sticky top-0 z-20 flex items-center justify-between gap-3 px-4 sm:px-8">
          <a href="/" data-testid="home-logo-link-mobile" className="flex items-center group md:hidden shrink-0" aria-label="GetInterviewed home">
            <BrandLockup size={28} textClass="text-base" wordClass="hidden min-[380px]:inline" />
          </a>
          <NextStep loaded={loaded} baseCv={baseCv} applications={applications} view={view} onGo={go} />
          <div className="ml-auto flex items-center gap-1.5 sm:gap-3 shrink-0">
            <button data-testid="kesh-open-mobile" onClick={() => window.dispatchEvent(new Event("kesh-open"))} aria-label="Chat with Kesh" title="Ask Kesh"
              className="w-10 h-10 rounded-full bg-kesh text-onbrand flex items-center justify-center font-display font-bold text-sm hover:bg-kesh-hover transition-colors">
              K
            </button>
            <ThemeToggle />
            <button data-testid="support-us-button-mobile" onClick={() => setSupportOpen(true)} aria-label="Support us"
              className="hidden sm:flex md:hidden w-10 h-10 rounded-xl items-center justify-center text-brand hover:bg-brand-soft transition-colors">
              <Heart className="w-4 h-4" />
            </button>
            <GoalChip stats={stats} />
          </div>
        </header>

        <MobileNav view={view} onGo={go} isAdmin={user?.is_admin} onSupport={() => setSupportOpen(true)} />

        <main className="flex-1 min-w-0 px-4 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:p-8 lg:p-10 max-w-6xl w-full mx-auto" data-testid={`dashboard-view-${view}`}>
          {!loaded ? (
            <div className="flex items-center justify-center py-32"><Loader2 className="w-7 h-7 text-brand animate-spin" /></div>
          ) : view === "home" ? (
            <Home user={user} baseCv={baseCv} applications={applications} onGo={go} onOpenApp={openApp} onCvChange={refresh} />
          ) : view === "profile" ? (
            <Profile baseCv={baseCv} onCvChange={refresh} onGo={go} />
          ) : view === "studio" ? (
            <Studio appId={appId} baseCv={baseCv} onCvChange={refresh} onChanged={refresh} onOpenApp={openApp} onExit={go} />
          ) : view === "tracker" ? (
            <Tracker applications={applications} stats={stats} onChange={refresh} onNew={() => openApp(null)} onOpen={openApp} />
          ) : view === "mycv" ? (
            <MyCv baseCv={baseCv} onUploaded={refresh} />
          ) : view === "career" ? (
            <CareerJourney onNavigate={go} applications={applications} />
          ) : view === "community" ? (
            <Community user={user} />
          ) : view === "voice" ? (
            <VoiceRefine baseCv={baseCv} onNeedCv={() => go("mycv")} onApplied={refresh} />
          ) : view === "classic" ? (
            <NewApplication baseCv={baseCv} onNeedCv={() => go("mycv")} onCreated={refresh} />
          ) : (
            <PrivacyCentre onChanged={refresh} />
          )}
        </main>
        <footer className="border-t border-line py-4 px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1.5 text-center">
          <nav className="flex items-center gap-4 flex-wrap justify-center" data-testid="footer-legal-links">
            <a href="/terms" target="_blank" rel="noreferrer" className="text-sm text-faint hover:text-brand transition-colors">Terms</a>
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-sm text-faint hover:text-brand transition-colors">Privacy Policy</a>
            <a href="/data-retention" target="_blank" rel="noreferrer" className="text-sm text-faint hover:text-brand transition-colors">Data Retention</a>
            <a href="/pdpa" target="_blank" rel="noreferrer" className="text-sm text-faint hover:text-brand transition-colors">PDPA Statement</a>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="dashboard-footer-linkedin" className="inline-flex items-center gap-1.5 text-sm text-faint hover:text-brand transition-colors">
              <Linkedin className="w-4 h-4" aria-hidden="true" /> Follow us
            </a>
          </nav>
          <span className="text-sm text-faint hidden sm:inline">·</span>
          <span className="text-sm text-faint" data-testid="footer-credit">Created by rukeshvaran</span>
        </footer>
      </div>
      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
      <ConsentGate />
    </div>
  );
}

function GoalChip({ stats }) {
  const done = stats?.interviews_this_month ?? 0;
  return (
    <div className="flex items-center gap-3" data-testid="interview-goal-progress">
      <Trophy className="w-4 h-4 text-kesh hidden min-[400px]:block" aria-hidden="true" />
      <div className="text-right">
        <div className="text-xs text-faint hidden min-[400px]:block">Interviews this month</div>
        <div className="text-sm font-semibold"><span className="text-kesh-text">{done}</span><span className="text-faint font-normal"> logged</span></div>
      </div>
    </div>
  );
}

function NextStep({ loaded, baseCv, applications, view, onGo }) {
  if (!loaded || view === "home" || view === "studio") return null;
  const step = !baseCv ? ["mycv", "Upload your CV"] : !applications.length ? ["studio", "Tailor your first application"] : null;
  if (!step || view === step[0]) return null;
  return (
    <button data-testid="next-step-button" onClick={() => onGo(step[0])}
      className="hidden lg:inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-brand-soft text-brand-text text-sm font-semibold hover:bg-brand/15 transition-colors">
      <span className="text-xs font-semibold uppercase tracking-wide text-brand">Next step</span> {step[1]} <ArrowRight className="w-4 h-4" aria-hidden="true" />
    </button>
  );
}
